/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo } from 'react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { Box, lighten } from '@mui/material';
import { EditorContent, EditorContext, findParentNode, useEditor } from '@tiptap/react';
import { TextStyleKit } from '@tiptap/extension-text-style';
import TiptapToolbar from './Toolbar';
import LinkBubbleMenu from './Menus/LinkBubbleMenu';
import FileHandler from '@tiptap/extension-file-handler';
import { Focus, Gapcursor, Selection, Placeholder } from '@tiptap/extensions';
import getEditorSx from './style';
import CustomSpan from './Extensions/CustomSpan';
import { CustomParagraph } from './Extensions/CustomParagraph';
import { CustomBulletList, CustomOrderedList } from './Extensions/CustomList';
import CustomLink from './Extensions/CustomLink';
import { TableKit } from '@tiptap/extension-table';
import {
	CustomTable,
	CustomTableCell,
	CustomTableHeader,
	CustomTableRow,
} from './Extensions/CustomTable';
import CustomDiv from './Extensions/CustomDiv';
import Config from './Extensions/Config';
import { CustomImage } from './Extensions/CustomImage';
import { TemplateValueTypingGuard } from './Extensions/TemplateValueTypingGuard';
import { LineHeight } from './Extensions/LineHeight';
import { TabIndent } from './Extensions/TabIndent';

const TiptapEditor = ({
	value = '',
	onChange,
	onBlur,
	disabled,
	error,
	placeholder,
	padding,
	minHeight = '200px',
	maxHeight = '100%',
	config = {},
	timeoutMillis = 100,
}) => {
	const { toolbar, ...editorConfig } = config;
	const { image, align, table } = toolbar ?? {};

	const configExtension = useMemo(
		() =>
			Config.configure({
				toolbar: toolbar ?? {},
				editor: editorConfig ?? {},
			}),
		[toolbar, editorConfig],
	);

	const editor = useEditor({
		editable: !disabled,
		content: value,
		extensions: [
			configExtension,
			StarterKit.configure({
				link: false,
				paragraph: false,
				bulletList: false,
				orderedList: false,
			}),
			...(image?.show
				? [
						CustomImage.configure({
							inline: true,
							allowBase64: true,
							resize: {
								enabled: true,
								minWidth: 20,
								minHeight: 20,
								alwaysPreserveAspectRatio: true,
							},
						}),
						// Image.configure({
						// 	inline: true,
						// 	allowBase64: true,
						// 	resize: {
						// 		enabled: true,
						// 		minWidth: 20,
						// 		minHeight: 20,
						// 		alwaysPreserveAspectRatio: true,
						// 	},
						// }),
					]
				: []),
			TextAlign.configure({
				...(!align?.show && { alignments: ['left'] }),
				types: ['heading', 'paragraph'],
			}),
			TextStyleKit.configure({
				lineHeight: false,
			}),
			...(image?.show
				? [
						FileHandler.configure({
							allowedMimeTypes: ['image/png', 'image/jpeg', 'image/gif'],
							onDrop: (currentEditor, files, pos) => {
								files.forEach((file) => {
									const fileReader = new FileReader();

									fileReader.readAsDataURL(file);
									fileReader.onload = () => {
										currentEditor
											.chain()
											.insertContentAt(pos, {
												type: 'image',
												attrs: {
													src: fileReader.result,
												},
											})
											.focus()
											.run();
									};
								});
							},
							onPaste: (currentEditor, files, htmlContent) => {
								files.forEach((file) => {
									if (htmlContent) {
										// if there is htmlContent, stop manual insertion & let other extensions handle insertion via inputRule
										// you could extract the pasted file from this url string and upload it to a server for example
										return false;
									}

									const fileReader = new FileReader();

									fileReader.readAsDataURL(file);
									fileReader.onload = () => {
										currentEditor
											.chain()
											.insertContentAt(currentEditor.state.selection.anchor, {
												type: 'image',
												attrs: {
													src: fileReader.result,
												},
											})
											.focus()
											.run();
									};
								});
							},
						}),
					]
				: []),
			...(table?.show
				? [
						Gapcursor,
						TableKit.configure({
							table: false,
							tableRow: false,
							tableCell: false,
							tableHeader: false,
							resizable: table.resizable ?? true,
						}),
						CustomTable.configure({
							renderWrapper: true,
							resizable: table.resizable ?? true,
							cellMinWidth: 100,
						}),
						CustomTableCell.configure({
							defaultBorderColor: table?.defaultBorderColor,
							defaultBorders: table?.defaultBorders,
						}),
						CustomTableHeader.configure({
							defaultBorderColor: table?.defaultBorderColor,
							defaultBorders: table?.defaultBorders,
						}),
						CustomTableRow,
					]
				: []),
			// Selection.configure({
			// 	className: 'editor-selection',
			// }),
			Focus.configure({
				className: 'has-focus',
				mode: 'deepest',
			}),
			...(placeholder
				? [
						Placeholder.configure({
							placeholder,
						}),
					]
				: []),
			CustomSpan,
			CustomParagraph,
			CustomBulletList,
			CustomOrderedList,
			CustomLink.configure({
				openOnClick: false,
				autolink: true,
				defaultProtocol: 'https',
				enableClickSelection: true,
				linkOnPaste: true,
			}),
			CustomDiv,
			LineHeight,
			TabIndent,
			TemplateValueTypingGuard.configure({}),
		],
		onUpdate({ editor, transaction, appendedTransactions }) {
			onChange?.(editor.getHTML(), editor.getText());
		},
		onBlur({ editor }) {
			setTimeout(() => onBlur?.(editor.getHTML()), timeoutMillis);
		},
		immediatelyRender: false,
	});

	const providerValue = useMemo(() => ({ editor }), [editor]);

	useEffect(() => {
		if (!editor) return;

		const current = editor.getHTML();
		if (value !== current) {
			editor.commands.setContent(value, { emitUpdate: false });
		}
		editor.setEditable(!disabled);
	}, [value, editor, disabled]);

	useEffect(() => {
		if (!editor) return;

		editor.storage.config.toolbar = toolbar ?? {};
		editor.storage.config.editor = editorConfig ?? {};

		editor.view.dispatch(editor.state.tr);
	}, [editor, toolbar, editorConfig]);

	return (
		<EditorContext.Provider value={providerValue}>
			<Box
				sx={(theme) => ({
					display: 'flex',
					flexDirection: 'column',
					flexGrow: 1,
					'& .tiptap-editor': {
						flex: 1,
						overflowY: 'auto',
						minHeight,
						maxHeight,
						borderRadius: '4px',
						borderTopLeftRadius: 0,
						borderTopRightRadius: 0,
						padding: padding ?? '12px 15px',
						border: `1px solid ${error ? '#d32f2f' : '#ccc'}`,
						backgroundColor: '#fff',
						color: 'rgba(0, 0, 0, 0.87)',
						cursor: 'text',
						// '& .ProseMirror-selectednode': {
						// 	outline: `3px solid ${lightFolioBlue}`,
						// },
					},
					...getEditorSx(theme),
				})}
			>
				<LinkBubbleMenu />
				{/* <TableBubbleMenu /> */}
				{/* <DivBubbleMenu />
				<TemplateBubbleMenu /> */}
				<TiptapToolbar />
				<EditorContent
					className='tiptap-editor'
					editor={editor}
					onMouseDown={(e) => {
						const closestEditor = e.target.closest('.tiptap');
						if (!closestEditor) {
							e.preventDefault();
							editor?.chain().focus().run();
						}
					}}
				/>
			</Box>
		</EditorContext.Provider>
	);
};

export default TiptapEditor;
